import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import * as CampaignContentAPI from "../../services/api/CampaignContent";
import {
  MarketingCampaignDoc,
  MarketingCampaignStatus,
} from "@pollyslack/hub/src/types";
import { CampaignForm } from "./CampaignForm";
import { TestDMModal } from "./TestDMModal";

type ViewMode = "list" | "create" | "edit" | "duplicate";

const STATUS_BADGE_CLASS: Record<MarketingCampaignStatus, string> = {
  [MarketingCampaignStatus.DRAFT]: "badge-secondary",
  [MarketingCampaignStatus.ACTIVE]: "badge-success",
  [MarketingCampaignStatus.ARCHIVED]: "badge-dark",
};

export const CampaignBuilderTab: React.FC = () => {
  const [campaigns, setCampaigns] = useState<MarketingCampaignDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [editingCampaign, setEditingCampaign] =
    useState<MarketingCampaignDoc | null>(null);
  const [testingCampaign, setTestingCampaign] =
    useState<MarketingCampaignDoc | null>(null);
  const [duplicatingCampaign, setDuplicatingCampaign] =
    useState<MarketingCampaignDoc | null>(null);

  async function loadCampaigns() {
    setLoading(true);
    try {
      const data = await CampaignContentAPI.listCampaigns(
        statusFilter || undefined,
      );
      setCampaigns(data);
    } catch (error: any) {
      toast.error(`Failed to load campaigns: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCampaigns();
  }, [statusFilter]);

  function handleCreate() {
    setEditingCampaign(null);
    setViewMode("create");
  }

  function handleEdit(campaign: MarketingCampaignDoc) {
    if (campaign.status === MarketingCampaignStatus.ACTIVE) {
      if (
        !window.confirm(
          `"${campaign.identifier}" is active and may already be enrolled to users. Edits will affect future sends. Continue?`,
        )
      ) {
        return;
      }
    }
    setEditingCampaign(campaign);
    setViewMode("edit");
  }

  /**
   * Opens the create form prefilled with this campaign's content, so the
   * copy gets a real identifier instead of a random "-copy-xxxx" suffix
   * (PS-5765). Nothing is saved until the admin hits Create Draft.
   */
  function handleDuplicate(campaign: MarketingCampaignDoc) {
    const baseIdentifier = campaign.identifier.replace(
      /-copy(-[a-z0-9]+)?$/,
      "",
    );
    setDuplicatingCampaign({
      ...campaign,
      identifier: `${baseIdentifier}-copy`,
    });
    setViewMode("duplicate");
  }

  async function handleActivate(campaign: MarketingCampaignDoc) {
    try {
      await CampaignContentAPI.activateCampaign(campaign._id);
      toast.success(`Campaign "${campaign.identifier}" activated`);
      loadCampaigns();
    } catch (error: any) {
      toast.error(error.message || "Failed to activate");
    }
  }

  async function handleArchive(campaign: MarketingCampaignDoc) {
    if (
      !window.confirm(
        `Archive campaign "${campaign.identifier}"? This will prevent it from being sent.`,
      )
    ) {
      return;
    }
    try {
      await CampaignContentAPI.archiveCampaign(campaign._id);
      toast.success(`Campaign "${campaign.identifier}" archived`);
      loadCampaigns();
    } catch (error: any) {
      toast.error(error.message || "Failed to archive");
    }
  }

  function handleFormSave() {
    setViewMode("list");
    loadCampaigns();
  }

  function handleFormCancel() {
    setViewMode("list");
  }

  if (viewMode === "create") {
    return <CampaignForm onSave={handleFormSave} onCancel={handleFormCancel} />;
  }

  if (viewMode === "duplicate" && duplicatingCampaign) {
    return (
      <CampaignForm
        initialValues={duplicatingCampaign}
        onSave={handleFormSave}
        onCancel={handleFormCancel}
      />
    );
  }

  if (viewMode === "edit" && editingCampaign) {
    return (
      <CampaignForm
        campaign={editingCampaign}
        onSave={handleFormSave}
        onCancel={handleFormCancel}
      />
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center">
          <label className="mr-2 mb-0 font-weight-bold">Filter:</label>
          <select
            className="form-control form-control-sm"
            style={{ width: 150 }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <button className="btn btn-primary btn-sm" onClick={handleCreate}>
          + Create New Campaign
        </button>
      </div>

      {loading ? (
        <div className="text-center py-4">
          <span className="spinner-border spinner-border-sm" role="status" />
          <span className="ml-2">Loading campaigns...</span>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center text-muted py-4">
          No campaigns found.{" "}
          <button className="btn btn-link p-0" onClick={handleCreate}>
            Create your first campaign
          </button>
        </div>
      ) : (
        <table className="table table-sm table-hover">
          <thead>
            <tr>
              <th>Identifier</th>
              <th>Header</th>
              <th>Primary CTA</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c) => (
              <tr key={c._id}>
                <td>
                  <code>{c.identifier}</code>
                </td>
                <td
                  style={{
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {c.headerText}
                </td>
                <td>
                  <small>{c.primaryCTA?.type}</small>
                </td>
                <td>
                  <span
                    className={`badge ${
                      STATUS_BADGE_CLASS[c.status] || "badge-light"
                    }`}
                  >
                    {c.status}
                  </span>
                </td>
                <td>
                  <small>{new Date(c.createdAt).toLocaleDateString()}</small>
                </td>
                <td>
                  <button
                    className="btn btn-outline-primary btn-sm mr-1"
                    onClick={() => handleEdit(c)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm mr-1"
                    onClick={() => handleDuplicate(c)}
                  >
                    Dup
                  </button>
                  <button
                    className="btn btn-outline-info btn-sm mr-1"
                    onClick={() => setTestingCampaign(c)}
                  >
                    Test
                  </button>
                  {c.status === MarketingCampaignStatus.DRAFT && (
                    <button
                      className="btn btn-outline-success btn-sm mr-1"
                      onClick={() => handleActivate(c)}
                    >
                      Activate
                    </button>
                  )}
                  {c.status !== MarketingCampaignStatus.ARCHIVED && (
                    <button
                      className="btn btn-outline-dark btn-sm"
                      onClick={() => handleArchive(c)}
                    >
                      Archive
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {testingCampaign && (
        <TestDMModal
          campaign={testingCampaign}
          isOpen={!!testingCampaign}
          onClose={() => setTestingCampaign(null)}
        />
      )}
    </div>
  );
};
